import pick from 'lodash/pick';
import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Link } from 'react-router-dom';
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd';
import { Button, Icon } from 'semantic-ui-react';
import { closePopup, usePopup } from '../../lib/popup';

import Paths from '../../constants/Paths';
import DroppableTypes from '../../constants/DroppableTypes';
import AddStep from '../Boards/AddStep';
import EditStep from '../Boards/EditStep';

import styles from './Sidebar.module.scss';
import globalStyles from '../../styles.module.scss';

const Sidebar = React.memo(({ projectId, items, currentId, canEdit, onCreate, onUpdate, onMove, onDelete }) => {
  if (!projectId) {
    return null;
  }

  const handleDragStart = useCallback(() => {
    document.body.classList.add(globalStyles.dragging);
    closePopup();
  }, []);

  const handleDragEnd = useCallback(
    ({ draggableId, source, destination }) => {
      document.body.classList.remove(globalStyles.dragging);

      if (!destination || source.index === destination.index) {
        return;
      }

      onMove(draggableId, destination.index);
    },
    [onMove],
  );

  const handleUpdate = useCallback(
    (id, data) => {
      onUpdate(id, data);
    },
    [onUpdate],
  );

  const handleDelete = useCallback(
    (id) => {
      onDelete(id);
    },
    [onDelete],
  );

  const AddPopup = usePopup(AddStep);
  const EditPopup = usePopup(EditStep);

  const itemsNode = items.map((item, index) => (
    <Draggable
      key={item.id}
      draggableId={item.id}
      index={index}
      isDragDisabled={!item.isPersisted || !canEdit}
    >
      {({ innerRef, draggableProps, dragHandleProps }) => (
        <div {...draggableProps} ref={innerRef} className={styles.wrapper}>
          <div className={classNames(styles.item, item.id === currentId && styles.itemActive)}>
            {item.isPersisted ? (
              <>
                <Link
                  {...dragHandleProps}
                  to={Paths.BOARDS.replace(':id', item.id)}
                  title={item.name}
                  className={styles.link}
                >
                  <span className={styles.name}>{item.name}</span>
                  {item.notificationsTotal > 0 && (
                    <span className={styles.notifications}>{item.notificationsTotal}</span>
                  )}
                </Link>
                {canEdit && (
                  <EditPopup
                    defaultData={pick(item, 'name')}
                    onUpdate={(data) => handleUpdate(item.id, data)}
                    onDelete={() => handleDelete(item.id)}
                  >
                    <Button className={classNames(styles.editButton, styles.target)}>
                      <Icon fitted name="pencil" size="small" />
                    </Button>
                  </EditPopup>
                )}
              </>
            ) : (
              <span {...dragHandleProps} className={styles.link}>
                <span className={styles.name}>{item.name}</span>
              </span>
            )}
          </div>
        </div>
      )}
    </Draggable>
  ));

  return (
    <div className={styles.sidebar}>
      <div className={styles.header}>
        <span className={styles.headerText}>Boards</span>
      </div>
      <div className={styles.content}>
        <DragDropContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
          <Droppable droppableId="sidebar-boards" type={DroppableTypes.BOARD} direction="vertical">
            {({ innerRef, droppableProps, placeholder }) => (
              <div {...droppableProps} ref={innerRef} className={styles.items}>
                {itemsNode}
                {placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
        {canEdit && (
          <div className={styles.addButtonWrapper}>
            <AddPopup onCreate={onCreate}>
              <Button icon="plus" className={styles.addButton} content="Add Board" fluid />
            </AddPopup>
          </div>
        )}
      </div>
    </div>
  );
});

Sidebar.propTypes = {
  projectId: PropTypes.string,
  items: PropTypes.array.isRequired,
  currentId: PropTypes.string,
  canEdit: PropTypes.bool.isRequired,
  onCreate: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  onMove: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

Sidebar.defaultProps = {
  projectId: undefined,
  currentId: undefined,
};

export default Sidebar;
